/* mz-collection.js: the MÂZIA product listing (sections/mz-collection-main.liquid) on collection and search pages.
   The section renders every product of the page's set (up to 250); this script filters, counts, sorts and pages
   them with the design's rules (spec 04 §2.1):
   - pills: one sub-category / category at a time (collection membership, data-colls); counts never change;
   - materials: product tags, several at once, OR inside the group; price bands: the price the card shows;
   - counts: a material counts the pill's products in the chosen band, a band the pill's products in the chosen
     materials; the desktop rows with nothing to show are disabled;
   - sort: Featured (the server's order), Newest, price up / down; Load more adds 24 (36 in the six-column view);
   - the desktop editorial tile shows with Featured, the first pill and no filters.
   State lives in the URL (?material=…&price=…&sort=…&category=…, sub-categories change the path and the tab
   title) with history.replaceState, and the number of pieces shown in history.state, so Back and reloads return
   to the same view. After a change of pieces or order the grid's top is brought back under the sticky toolbar.
   Phones first; MZ.isDesktop() picks the desktop wording and rules.
   The section loads this file as an async module right after </mz-plp>, so it can run before mz-theme.js (window.MZ):
   every MZ call has a fallback. */

const SORTS = ['featured', 'newest', 'price-asc', 'price-desc'];
const VIEWS = ['2', '4', '6'];
const VIEW_KEY = 'mz-plp-view';
const STATE_KEY = 'mzPlpShown';
const PARAMS = ['material', 'price', 'sort', 'category'];
// The editorial tile follows this many cards in the DOM: row 1 and the two cards left of it in row 2
const TILE_AFTER = 6;
// Card photo sizes per desktop view (the card renders the four-column ones): bigger photos for two columns.
// [d] / [m] take each photo's cover factor in the desktop / phone card frame, as snippets/mz-product-card.liquid
// prints them: a photo wider than its frame (a square one in the 4:5 frame) is drawn wider than the card
const SIZES = {
  2: '(min-width: 750px) calc((100vw - 80px) / 2[d]), calc(50vw[m])',
  4: '(min-width: 990px) calc((100vw - 112px) / 4[d]), (min-width: 750px) calc((100vw - 96px) / 3[d]), calc(50vw[m])',
  6: '(min-width: 990px) calc((100vw - 124px) / 6[d]), (min-width: 750px) calc((100vw - 100px) / 4[d]), calc(50vw[m])',
};

// A card frame from the theme settings (--mz-card-ratio: 4/5) as width / height
function frameRatio(name, fallback) {
  const [w, h] = window.getComputedStyle(document.documentElement).getPropertyValue(name).split('/').map(Number);
  return w > 0 && h > 0 ? w / h : fallback;
}

// ' * 1.25' for a photo 1.25 times as wide as its frame (rounded as the card rounds it), '' when it is not wider
function coverFactor(ratio, frame) {
  const factor = Math.round(Math.max(1, ratio / frame) * 100) / 100;
  return factor > 1 ? ` * ${factor}` : '';
}

const desktopQuery = window.matchMedia('(min-width: 750px)');
const reducedQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
const isDesktop = () => desktopQuery.matches;

const fill = (text, values) =>
  Object.keys(values).reduce((out, key) => out.split(`[${key}]`).join(String(values[key])), text || '');

function readStore(key) {
  try {
    return sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function writeStore(key, value) {
  try {
    sessionStorage.setItem(key, value);
  } catch (e) {
    /* private mode: the view simply isn't remembered */
  }
}

// Page scroll lock for the dialogs (MZ.lockScroll in mz-theme.js, keyed; the bare class until it has loaded)
function lockPage() {
  if (window.MZ?.lockScroll) window.MZ.lockScroll('mz-plp');
  else document.documentElement.classList.add('mz-scroll-locked');
}

function unlockPage() {
  if (window.MZ?.unlockScroll) window.MZ.unlockScroll('mz-plp');
  else document.documentElement.classList.remove('mz-scroll-locked');
}

// Jump the page to `top`: html has scroll-behavior: smooth (Horizon's base.css), so it is switched off for this call
function jumpTo(top) {
  const root = document.documentElement;
  const previous = root.style.scrollBehavior;
  root.style.scrollBehavior = 'auto';
  window.scrollTo(0, top);
  root.style.scrollBehavior = previous;
}

class MzPlp extends HTMLElement {
  connectedCallback() {
    if (this.mzReady) return;
    const configEl = this.querySelector('[data-mz-plp-config]');
    this.grid = this.querySelector('[data-mz-plp-grid]');
    if (!configEl || !this.grid) return;
    try {
      this.config = JSON.parse(configEl.textContent);
    } catch (e) {
      return;
    }
    this.mzReady = true;
    this.labels = this.config.labels || {};
    this.bands = this.config.bands || [];

    this.tile = this.grid.querySelector('[data-mz-plp-tile]');
    this.items = [...this.grid.querySelectorAll('[data-mz-plp-item]')].map((el) => ({
      el,
      i: Number(el.dataset.i) || 0,
      colls: el.dataset.colls || '',
      tags: ` ${el.dataset.tags || ''} `,
      price: Number(el.dataset.price) || 0,
      date: Number(el.dataset.date) || 0,
    }));
    this.pills = [...this.querySelectorAll('[data-mz-plp-pill]')];
    this.rail = this.querySelector('[data-mz-plp-rail]');
    this.bar = this.querySelector('[data-mz-plp-bar]');
    this.materialInputs = [...this.querySelectorAll('[data-mz-plp-material]')];
    this.bandInputs = [...this.querySelectorAll('[data-mz-plp-band]')];
    this.sortRadios = [...this.querySelectorAll('[data-mz-plp-sort-radio]')];
    this.sortOptions = [...this.querySelectorAll('[data-mz-plp-sort-opt]')];
    this.viewButtons = [...this.querySelectorAll('[data-mz-plp-view]')];
    this.sortWrap = this.querySelector('[data-mz-plp-sort]');
    this.sortMenu = this.querySelector('[data-mz-plp-sort-menu]');
    this.sortToggle = this.querySelector('[data-mz-plp-sort-toggle]');
    this.more = this.querySelector('[data-mz-plp-more]');
    this.moreButton = this.querySelector('[data-mz-plp-more-btn]');
    this.showing = this.querySelector('[data-mz-plp-showing]');
    this.progress = this.querySelector('[data-mz-plp-progress]');
    this.empty = this.querySelector('[data-mz-plp-empty]');
    this.status = this.querySelector('[data-mz-plp-status]');
    this.dialogs = {};
    this.querySelectorAll('[data-mz-plp-dialog]').forEach((dialog) => {
      this.dialogs[dialog.dataset.mzPlpDialog] = dialog;
    });

    const view = readStore(VIEW_KEY);
    this.state = { view: VIEWS.includes(view) ? Number(view) : 4 };
    Object.assign(this.state, this.readUrl());

    this.onClick = this.onClick.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
    this.onBreakpoint = this.onBreakpoint.bind(this);
    this.onPopState = this.onPopState.bind(this);
    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChange);
    this.addEventListener('keydown', this.onKeydown);
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onDocumentKeydown);
    desktopQuery.addEventListener('change', this.onBreakpoint);
    window.addEventListener('popstate', this.onPopState);
    Object.values(this.dialogs).forEach((dialog) => this.wireDialog(dialog));

    // The toolbar's height for the listing's focus stops (--mz-plp-bar-h, assets/mz-collection.css section 3): a
    // control focused from below stops under the sticky toolbar, not behind it, however many rows the bar wraps to
    if (this.bar && 'ResizeObserver' in window) {
      this.barObserver = new ResizeObserver(() => {
        if (this.bar.offsetHeight) this.style.setProperty('--mz-plp-bar-h', `${this.bar.offsetHeight}px`);
      });
      this.barObserver.observe(this.bar);
    }

    this.render();
    this.grid.removeAttribute('data-pending');
    this.saveShown();
    this.revealActivePill();

    // A tap on Filter, Sort or Load more made before this file ran (kept by the section's inline script)
    const pending = window.mzPlpPending;
    window.mzPlpPending = null;
    if (pending instanceof HTMLElement && this.contains(pending)) window.requestAnimationFrame(() => pending.click());
  }

  disconnectedCallback() {
    if (!this.mzReady) return;
    this.barObserver?.disconnect();
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onDocumentKeydown);
    desktopQuery.removeEventListener('change', this.onBreakpoint);
    window.removeEventListener('popstate', this.onPopState);
    unlockPage();
  }

  /* ---------- State ---------- */

  step() {
    return this.state && this.state.view === 6 && isDesktop() ? this.config.stepSmall || 36 : this.config.step || 24;
  }

  readUrl() {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    const known = this.materialInputs.map((input) => input.value);
    const wanted = (params.get('material') || '').split(',').map((value) => value.trim());
    const mats = known.filter((value) => wanted.includes(value));
    const band = this.bands.findIndex((b) => b.key === params.get('price'));
    const sort = SORTS.includes(params.get('sort')) ? params.get('sort') : 'featured';
    let pill = this.config.active || '';
    if (this.config.mode === 'cat') {
      const category = params.get('category') || '';
      pill = this.pills.some((el) => el.dataset.mzPlpPill === category) ? category : '';
    }
    const saved = Number(window.history.state?.[STATE_KEY]) || 0;
    const step = this.state?.view === 6 && isDesktop() ? this.config.stepSmall : this.config.step;
    return { pill, mats, band, sort, shown: Math.max(saved, step || 24) };
  }

  writeUrl() {
    const state = this.state;
    const url = new URL(window.location.href);
    if (this.config.mode === 'sub') {
      const target = state.pill ? this.pillHref(state.pill) : this.config.allUrl;
      if (target) url.pathname = new URL(target, window.location.origin).pathname;
    }
    const params = url.searchParams;
    PARAMS.forEach((key) => params.delete(key));
    params.delete('page');
    params.delete('sort_by');
    if (state.mats.length) params.set('material', state.mats.join(','));
    if (state.band >= 0 && this.bands[state.band]) params.set('price', this.bands[state.band].key);
    if (state.sort !== 'featured') params.set('sort', state.sort);
    if (this.config.mode === 'cat' && state.pill) params.set('category', state.pill);
    const href = url.pathname + (params.toString() ? `?${params.toString().replace(/%2C/gi, ',')}` : '') + url.hash;
    window.history.replaceState({ ...(window.history.state || {}), [STATE_KEY]: state.shown }, '', href);

    // A sub-category is its own collection page: the tab title and the canonical link follow the path, as on a reload
    if (this.config.mode === 'sub') {
      const pill = this.pills.find((el) => el.dataset.mzPlpPill === state.pill);
      if (pill?.dataset.mzPlpDocTitle) document.title = pill.dataset.mzPlpDocTitle;
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.setAttribute('href', new URL(url.pathname, canonical.href).href);
    }
  }

  saveShown() {
    window.history.replaceState({ ...(window.history.state || {}), [STATE_KEY]: this.state.shown }, '');
  }

  pillHref(handle) {
    const el = this.pills.find((pill) => pill.dataset.mzPlpPill === handle);
    return el ? el.getAttribute('href') : '';
  }

  activeFilters() {
    return this.state.mats.length + (this.state.band >= 0 ? 1 : 0);
  }

  update(changes, { announce = true, resetShown = true } = {}) {
    const before = this.listKey();
    const shownBefore = this.shown;
    Object.assign(this.state, changes);
    if (resetShown) this.state.shown = this.step();
    this.render();
    this.writeUrl();
    // Other pieces or another order, or fewer of them shown (back to the first 24): the page may be shorter now
    if (this.listKey() !== before || this.shown < shownBefore) this.keepGridInView();
    if (announce) this.announce();
  }

  // Which pieces the grid lists, and in what order (pill, materials, price band, sort)
  listKey() {
    const { pill, mats, band, sort } = this.state;
    return [pill, mats.join(','), band, sort].join('|');
  }

  // After a change of pieces or order (or back to fewer pieces) the page is often shorter, and the browser keeps the
  // scroll offset: when the grid's top has gone above the sticky toolbar (the shopper was scrolled into the grid),
  // bring it back under the toolbar with the usual gap, at once (also behind an open sheet or drawer). Otherwise the
  // page stays where it is. On short landscape screens the toolbar scrolls with the page (assets/mz-collection.css):
  // under the header then.
  keepGridInView() {
    if (!this.bar || !this.bar.offsetHeight) return;
    const stick = this.coveredTop();
    const top = this.grid.getBoundingClientRect().top;
    if (top >= stick) return;
    const gap = parseFloat(window.getComputedStyle(this.grid.parentElement).paddingTop) || 0;
    jumpTo(Math.max(0, window.scrollY + top - stick - gap));
  }

  /* ---------- Rendering ---------- */

  sorter(sort) {
    switch (sort) {
      case 'newest':
        return (a, b) => b.date - a.date || a.i - b.i;
      case 'price-asc':
        return (a, b) => a.price - b.price || a.i - b.i;
      case 'price-desc':
        return (a, b) => b.price - a.price || a.i - b.i;
      default:
        return (a, b) => a.i - b.i;
    }
  }

  render() {
    const state = this.state;
    const labels = this.labels;
    const desktop = isDesktop();
    const pillKey = state.pill ? `|${state.pill}|` : '';
    const inBand = (item, band) => (band.min == null || item.price >= band.min) && (band.max == null || item.price < band.max);
    const band = state.band >= 0 ? this.bands[state.band] : null;
    const matOk = (item) => !state.mats.length || state.mats.some((mat) => item.tags.includes(` ${mat} `));
    const priceOk = (item) => !band || inBand(item, band);

    const inPill = pillKey ? this.items.filter((item) => item.colls.includes(pillKey)) : this.items;
    const rows = inPill.filter((item) => matOk(item) && priceOk(item)).sort(this.sorter(state.sort));
    const total = rows.length;
    const shown = Math.min(state.shown, total);

    // Grid: the matching pieces in the chosen order, then the rest (hidden) in the featured order
    const matching = new Set(rows);
    const order = rows.concat(this.items.filter((item) => !matching.has(item)));
    const current = this.grid.querySelectorAll('[data-mz-plp-item]');
    if (order.some((item, index) => current[index] !== item.el)) {
      const fragment = document.createDocumentFragment();
      order.forEach((item) => fragment.append(item.el));
      this.grid.append(fragment);
    }
    // The editorial tile comes after the 6th card in the DOM, so the Tab order follows the reading order; its
    // grid-column / grid-row (assets/mz-collection.css) keep it in columns 3-4 of rows 2-3 wherever it sits
    if (this.tile) {
      const anchor = rows[Math.min(TILE_AFTER, rows.length) - 1]?.el;
      if (anchor && anchor.nextElementSibling !== this.tile) anchor.after(this.tile);
    }
    order.forEach((item, index) => {
      item.el.hidden = index >= shown;
      item.el.classList.remove('is-later');
    });
    this.grid.dataset.view = String(state.view);
    if (state.view !== (this.sizedView || 4)) this.applySizes(state.view);

    const filters = this.activeFilters();
    const tileOn =
      !!this.tile && state.sort === 'featured' && !state.pill && !filters && total >= (this.config.tileMin || 12);
    this.grid.classList.toggle('is-tile', tileOn);

    // Pills
    this.pills.forEach((pill) => {
      if (pill.dataset.mzPlpPill === state.pill) pill.setAttribute('aria-current', 'true');
      else pill.removeAttribute('aria-current');
    });

    // Filter rows: checked state, counts, disabled when empty (desktop)
    const showCounts = this.config.showCounts !== false;
    this.materialInputs.forEach((input) => {
      const on = state.mats.includes(input.value);
      const count = inPill.filter((item) => priceOk(item) && item.tags.includes(` ${input.value} `)).length;
      input.checked = on;
      input.disabled = desktop && count === 0 && !on;
      this.setCount(input, showCounts ? count : '');
    });
    this.bandInputs.forEach((input) => {
      if (!input.value) {
        input.checked = !band;
        return;
      }
      const b = this.bands.find((x) => x.key === input.value);
      const on = !!band && band.key === input.value;
      const count = b ? inPill.filter((item) => matOk(item) && inBand(item, b)).length : 0;
      input.checked = on;
      input.disabled = desktop && count === 0 && !on;
      this.setCount(input, showCounts ? count : '');
    });

    // Toolbar and counts
    const filterText = filters ? fill(labels.filterCount, { count: filters }) : labels.filter;
    this.querySelectorAll('[data-mz-plp-filter-label]').forEach((el) => (el.textContent = filterText));
    const countEl = this.querySelector('[data-mz-plp-count]');
    if (countEl) countEl.textContent = fill(total === 1 ? labels.countOne : labels.countOther, { count: total });
    const result = this.querySelector('[data-mz-plp-result]');
    if (result) {
      result.textContent = desktop
        ? fill(total === 1 ? labels.resultDesktopOne : labels.resultDesktopOther, { count: total })
        : total === 0
          ? labels.resultMobileNone
          : fill(total === 1 ? labels.resultMobileOne : labels.resultMobileOther, { count: total });
    }

    // Sort
    const sortValue = this.querySelector('[data-mz-plp-sort-value]');
    if (sortValue) sortValue.textContent = labels.sortDesktop?.[state.sort] || '';
    const sortValueMobile = this.querySelector('[data-mz-plp-sort-value-mobile]');
    if (sortValueMobile) sortValueMobile.textContent = labels.sortMobile?.[state.sort] || '';
    this.sortOptions.forEach((option) =>
      option.setAttribute('aria-checked', String(option.dataset.mzPlpSortOpt === state.sort))
    );
    this.sortRadios.forEach((radio) => (radio.checked = radio.value === state.sort));

    // View
    this.viewButtons.forEach((button) =>
      button.setAttribute('aria-pressed', String(button.dataset.mzPlpView === String(state.view)))
    );

    // Showing N of M, Load more, empty state
    if (this.more) {
      this.more.hidden = total === 0;
      this.more.classList.toggle('is-complete', shown >= total);
    }
    if (this.showing) this.showing.textContent = fill(labels.showing, { shown, total });
    if (this.progress) this.progress.style.width = `${total ? Math.round((shown / total) * 100) : 0}%`;
    if (this.moreButton) this.moreButton.hidden = shown >= total;
    if (this.empty) this.empty.hidden = total !== 0;

    this.total = total;
    this.shown = shown;
    // For other areas (analytics, chrome): what the listing shows now
    const detail = { total, shown, pill: state.pill, materials: [...state.mats], price: band ? band.key : '', sort: state.sort };
    if (window.MZ?.emit) window.MZ.emit('plp', detail);
    else document.dispatchEvent(new CustomEvent('mz:plp', { detail }));
  }

  applySizes(view) {
    this.sizedView = view;
    const frameDesktop = frameRatio('--mz-card-ratio', 4 / 5);
    const frameMobile = frameRatio('--mz-card-ratio-mobile', 1);
    this.grid.querySelectorAll('[data-mz-plp-item] img.mz-card__img').forEach((img) => {
      // width / height are the photo's own proportions (image_tag)
      const width = Number(img.getAttribute('width'));
      const height = Number(img.getAttribute('height'));
      const ratio = width > 0 && height > 0 ? width / height : 1;
      const sizes = SIZES[view]
        .split('[d]')
        .join(coverFactor(ratio, frameDesktop))
        .split('[m]')
        .join(coverFactor(ratio, frameMobile));
      img.setAttribute('sizes', sizes);
    });
  }

  setCount(input, count) {
    const el = input.closest('label')?.querySelector('[data-mz-plp-opt-count]');
    if (el) el.textContent = String(count);
  }

  announce() {
    if (!this.status) return;
    const labels = this.labels;
    const text = fill(this.total === 1 ? labels.countOne : labels.countOther, { count: this.total });
    window.clearTimeout(this.announceTimer);
    this.announceTimer = window.setTimeout(() => {
      this.status.textContent = text;
    }, 400);
  }

  // After Load more: focus goes to the first new piece's name link, so Tab carries on through the new cards (the
  // button now sits below them, and hides after the last batch). The page doesn't move; from the keyboard (a click
  // with no pointer, detail 0) it then scrolls just enough to show that card with its focus ring.
  focusPiece(index, fromKeyboard) {
    const item = this.grid.querySelectorAll('[data-mz-plp-item]:not([hidden])')[index];
    const link =
      item?.querySelector('a.mz-card__text') || item?.querySelector('a[href]:not([tabindex="-1"]), button:not([disabled])');
    if (!link) return;
    link.focus({ preventScroll: true });
    if (!fromKeyboard) return;
    const box = item.getBoundingClientRect();
    const top = this.coveredTop() + 16;
    // The bottom of the screen the WhatsApp button keeps clear (assets/mz-footer.css)
    const bottom =
      window.innerHeight - (parseFloat(window.getComputedStyle(document.documentElement).scrollPaddingBottom) || 0);
    let delta = 0;
    if (box.bottom > bottom) delta = Math.min(box.bottom - bottom, box.top - top);
    else if (box.top < top) delta = box.top - top;
    if (delta) jumpTo(window.scrollY + delta);
  }

  // How far down the screen the header and the sticky toolbar reach: page content is clear of them below this
  coveredTop() {
    const header =
      parseFloat(window.getComputedStyle(document.documentElement).getPropertyValue('--mz-top-offset')) || 0;
    const bar = this.bar;
    if (!bar || !bar.offsetHeight) return header;
    const barStyle = window.getComputedStyle(bar);
    // Short landscape screens: the toolbar scrolls with the page (assets/mz-collection.css)
    if (barStyle.position !== 'sticky') return header;
    return (parseFloat(barStyle.top) || 0) + bar.offsetHeight;
  }

  revealActivePill() {
    if (!this.rail) return;
    const chip = this.rail.querySelector('[aria-current="true"]');
    if (!chip) return;
    const overflow = chip.offsetLeft + chip.offsetWidth + 16 - (this.rail.scrollLeft + this.rail.clientWidth);
    if (overflow > 0) this.rail.scrollLeft += overflow;
  }

  /* ---------- Events ---------- */

  onClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const pill = target.closest('[data-mz-plp-pill]');
    if (pill) {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      // The pill already shown changes nothing: the listing stays as it is (no fall back to the first 24 pieces)
      const value = pill.dataset.mzPlpPill || '';
      if (value !== this.state.pill) this.update({ pill: value });
      return;
    }

    const view = target.closest('[data-mz-plp-view]');
    if (view) {
      const value = Number(view.dataset.mzPlpView);
      writeStore(VIEW_KEY, String(value));
      const shown = value === 6 ? Math.max(this.state.shown, this.config.stepSmall || 36) : this.state.shown;
      this.update({ view: value, shown }, { announce: false, resetShown: false });
      return;
    }

    const opener = target.closest('[data-mz-plp-open]');
    if (opener) {
      this.toggleSortMenu(false);
      this.openDialog(this.dialogs[opener.dataset.mzPlpOpen]);
      return;
    }

    if (target.closest('[data-mz-plp-close]')) {
      this.closeDialog(target.closest('dialog'));
      return;
    }

    const clear = target.closest('[data-mz-plp-clear]');
    if (clear) {
      // Desktop clears the filters, the sort and the pill (as designed), phones the filters. With nothing to clear
      // the listing stays as it is
      const desktop = isDesktop();
      if (!this.activeFilters() && (!desktop || (this.state.sort === 'featured' && !this.state.pill))) return;
      if (desktop) this.update({ mats: [], band: -1, sort: 'featured', pill: '' });
      else this.update({ mats: [], band: -1 });
      return;
    }

    if (target.closest('[data-mz-plp-sort-toggle]')) {
      this.toggleSortMenu(this.sortMenu?.hidden, event.detail === 0);
      return;
    }

    const option = target.closest('[data-mz-plp-sort-opt]');
    if (option) {
      this.toggleSortMenu(false);
      this.sortToggle?.focus();
      if (option.dataset.mzPlpSortOpt !== this.state.sort) this.update({ sort: option.dataset.mzPlpSortOpt });
      return;
    }

    // Phone sort sheet: a tap or click on any option applies it and closes the sheet, also the option already chosen
    // (no change event then). A tap on the label arrives here as a click on its radio. The click the browser sends
    // with an arrow key only moves the choice (onKeydown)
    const sortRadio = target.closest('[data-mz-plp-sort-radio]');
    if (sortRadio) {
      if (!this.sortByArrow) this.pickSort(sortRadio);
      return;
    }

    if (target.closest('[data-mz-plp-more-btn]')) {
      const from = this.shown;
      this.update({ shown: this.state.shown + this.step() }, { resetShown: false, announce: false });
      if (this.status) this.status.textContent = this.showing?.textContent || '';
      this.focusPiece(from, event.detail === 0);
      return;
    }

    // Desktop: clicking the chosen price band again clears it (phones have "Any price")
    const bandInput = target.closest('[data-mz-plp-band]');
    if (bandInput && bandInput.value && isDesktop()) {
      const band = this.state.band >= 0 ? this.bands[this.state.band] : null;
      if (band && band.key === bandInput.value) this.update({ band: -1 });
    }
  }

  onChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (input.matches('[data-mz-plp-material]')) {
      const mats = this.materialInputs.filter((el) => el.checked).map((el) => el.value);
      this.update({ mats });
    } else if (input.matches('[data-mz-plp-band]')) {
      if (!input.checked) return;
      this.update({ band: this.bands.findIndex((b) => b.key === input.value) });
    } else if (input.matches('[data-mz-plp-sort-radio]')) {
      // Taps are applied by onClick (which runs first, so the value is already the sort here); this catches a radio
      // checked without a click. Arrow keys only move the choice
      if (!input.checked || this.sortByArrow || input.value === this.state.sort) return;
      this.pickSort(input);
    }
  }

  // Phone sort sheet: apply the option (when it is another sort), close the sheet and go back to the top, as designed
  pickSort(radio) {
    this.closeDialog(this.dialogs.sort || radio.closest('dialog'));
    if (radio.value !== this.state.sort && SORTS.includes(radio.value)) this.update({ sort: radio.value });
    window.scrollTo(0, 0);
  }

  onKeydown(event) {
    // Phone sort sheet (also desktop browsers zoomed to phone width): the arrow keys only move the choice (the
    // browser checks the next radio and clicks it; onClick and onChange let that pass), Enter or Space applies the
    // checked option and closes the sheet (WCAG 3.2.2). The sheet's close event puts back the applied sort.
    const radio = event.target instanceof Element ? event.target.closest('[data-mz-plp-sort-radio]') : null;
    if (radio) {
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
        this.sortByArrow = true;
        window.setTimeout(() => {
          this.sortByArrow = false;
        }, 0);
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.pickSort(this.sortRadios.find((el) => el.checked) || radio);
      }
      return;
    }

    // Desktop sort menu: arrows, Home / End and Tab inside it (Esc: onDocumentKeydown)
    if (!this.sortMenu || this.sortMenu.hidden) return;
    if (!this.sortWrap?.contains(event.target)) return;
    const options = this.sortOptions;
    const index = options.indexOf(document.activeElement);
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const next = event.key === 'ArrowDown' ? index + 1 : index - 1;
      options[(next + options.length) % options.length]?.focus();
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      options[event.key === 'Home' ? 0 : options.length - 1]?.focus();
    } else if (event.key === 'Tab') {
      this.toggleSortMenu(false);
    }
  }

  onDocumentClick(event) {
    if (!this.sortMenu || this.sortMenu.hidden) return;
    if (this.sortWrap && event.target instanceof Node && this.sortWrap.contains(event.target)) return;
    this.toggleSortMenu(false);
  }

  // Esc closes the open sort menu wherever the focus is: Safari leaves it on the page after a click on Sort. Focus
  // goes back to the Sort button when it was in the menu or nowhere
  onDocumentKeydown(event) {
    if (event.key !== 'Escape' || !this.sortMenu || this.sortMenu.hidden) return;
    event.preventDefault();
    const active = document.activeElement;
    const inside = !active || active === document.body || !this.sortWrap || this.sortWrap.contains(active);
    this.toggleSortMenu(false);
    if (inside) this.sortToggle?.focus();
  }

  onBreakpoint() {
    this.toggleSortMenu(false);
    if (this.dialogs.sort?.open && isDesktop()) this.closeDialog(this.dialogs.sort);
    this.render();
  }

  onPopState() {
    Object.assign(this.state, this.readUrl());
    this.render();
  }

  /* ---------- Sort menu (desktop) ---------- */

  toggleSortMenu(open, fromKeyboard = false) {
    if (!this.sortMenu || !this.sortToggle) return;
    const next = !!open;
    if (this.sortMenu.hidden === !next) return;
    this.sortMenu.hidden = !next;
    this.sortToggle.setAttribute('aria-expanded', String(next));
    if (next && fromKeyboard) {
      (this.sortOptions.find((option) => option.getAttribute('aria-checked') === 'true') || this.sortOptions[0])?.focus();
    }
  }

  /* ---------- Dialogs: filter drawer / sheet, sort sheet ---------- */

  wireDialog(dialog) {
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.closeDialog(dialog);
    });
    // The dialog box fills the screen; a click on it (not on the panel) is a click on the scrim
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) this.closeDialog(dialog);
    });
    dialog.addEventListener('close', () => {
      dialog.classList.remove('is-open');
      delete dialog.dataset.closing;
      // The sort sheet closed without applying an option moved to with the arrow keys: the applied one is checked again
      if (dialog === this.dialogs.sort) this.sortRadios.forEach((el) => (el.checked = el.value === this.state.sort));
      if (!Object.values(this.dialogs).some((d) => d.open)) unlockPage();
    });
  }

  openDialog(dialog) {
    if (!dialog || dialog.open) return;
    Object.values(this.dialogs).forEach((d) => d !== dialog && d.open && d.close());
    dialog.showModal();
    lockPage();
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => dialog.classList.add('is-open')));
  }

  closeDialog(dialog) {
    if (!dialog || !dialog.open || dialog.dataset.closing) return;
    // Desktop drawer slides out (480ms); the phone sheets close at once, as designed
    if (isDesktop() && !reducedQuery.matches && dialog.classList.contains('is-open')) {
      dialog.dataset.closing = 'true';
      dialog.classList.remove('is-open');
      window.setTimeout(() => dialog.open && dialog.close(), 480);
    } else {
      dialog.close();
    }
  }
}

if (!customElements.get('mz-plp')) customElements.define('mz-plp', MzPlp);
